/**
 * ContentItemCard Component
 * Displays a content item in a card format for grid view
 */

import { forwardRef } from 'react'
import {
  FileText,
  Hash,
  Image as ImageIcon,
  Table,
  Mail,
  Presentation,
  MoreVertical,
  Eye,
  Pencil,
  Copy,
  Move,
  Trash2,
  GripVertical,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { FluentCard } from '@/components/layout/page-header'
import type { ContentItem, ContentItemType } from '@/lib/api/types'

// ============================================
// Types
// ============================================

interface ContentItemCardProps {
  item: ContentItem
  isSelected?: boolean
  isDragging?: boolean
  onClick?: () => void
  onDoubleClick?: () => void
  onView?: () => void
  onEdit?: () => void
  onDuplicate?: () => void
  onMove?: () => void
  onDelete?: () => void
  draggable?: boolean
  onDragStart?: (e: React.DragEvent) => void
  onDragEnd?: (e: React.DragEvent) => void
  className?: string
}

// ============================================
// Constants
// ============================================

const TYPE_ICONS: Record<ContentItemType, React.ElementType> = {
  Text: FileText,
  DataPoint: Hash,
  Image: ImageIcon,
  Table: Table,
  EmailSnippet: Mail,
  SlideElement: Presentation,
}

const TYPE_COLORS: Record<ContentItemType, string> = {
  Text: 'from-blue-400 to-blue-600',
  DataPoint: 'from-green-400 to-green-600',
  Image: 'from-purple-400 to-purple-600',
  Table: 'from-amber-400 to-amber-600',
  EmailSnippet: 'from-rose-400 to-rose-600',
  SlideElement: 'from-orange-400 to-orange-600',
}

const TYPE_LABELS: Record<ContentItemType, string> = {
  Text: 'Text',
  DataPoint: 'Data Point',
  Image: 'Image',
  Table: 'Table',
  EmailSnippet: 'Email',
  SlideElement: 'Slide',
}

// ============================================
// Helper Functions
// ============================================

function getPreviewContent(item: ContentItem): string | null {
  if (item.textContent?.content) {
    return item.textContent.content.slice(0, 100) + (item.textContent.content.length > 100 ? '...' : '')
  }
  if (item.dataPointContent?.currentValue) {
    return item.dataPointContent.currentValue
  }
  if (item.emailSnippetContent?.body) {
    // Strip HTML tags for preview
    const text = item.emailSnippetContent.body.replace(/<[^>]*>/g, '')
    return text.slice(0, 100) + (text.length > 100 ? '...' : '')
  }
  return null
}

function getPreviewImage(item: ContentItem): string | null {
  if (item.imageContent?.thumbnailUrl) {
    return item.imageContent.thumbnailUrl
  }
  if (item.imageContent?.assetUrl) {
    return item.imageContent.assetUrl
  }
  if (item.slideContent?.thumbnailUrl) {
    return item.slideContent.thumbnailUrl
  }
  return null
}

// ============================================
// ContentItemCard Component
// ============================================

export const ContentItemCard = forwardRef<HTMLDivElement, ContentItemCardProps>(
  (
    {
      item,
      isSelected = false,
      isDragging = false,
      onClick,
      onDoubleClick,
      onView,
      onEdit,
      onDuplicate,
      onMove,
      onDelete,
      draggable = true,
      onDragStart,
      onDragEnd,
      className,
    },
    ref
  ) => {
    const Icon = TYPE_ICONS[item.type] || FileText
    const color = TYPE_COLORS[item.type] || 'from-gray-400 to-gray-600'
    const typeLabel = TYPE_LABELS[item.type] || item.type
    const previewText = getPreviewContent(item)
    const previewImage = getPreviewImage(item)

    const handleDragStart = (e: React.DragEvent) => {
      e.dataTransfer.setData('application/json', JSON.stringify({ type: 'content-item', id: item.id }))
      e.dataTransfer.effectAllowed = 'move'
      onDragStart?.(e)
    }

    const menuContent = (
      <>
        {onView && (
          <ContextMenuItem onClick={onView}>
            <Eye className="h-4 w-4 mr-2" />
            View
          </ContextMenuItem>
        )}
        {onEdit && (
          <ContextMenuItem onClick={onEdit}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </ContextMenuItem>
        )}
        {onDuplicate && (
          <ContextMenuItem onClick={onDuplicate}>
            <Copy className="h-4 w-4 mr-2" />
            Duplicate
          </ContextMenuItem>
        )}
        {onMove && (
          <ContextMenuItem onClick={onMove}>
            <Move className="h-4 w-4 mr-2" />
            Move to Folder
          </ContextMenuItem>
        )}
        {onDelete && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              onClick={onDelete}
              className="text-red-600 dark:text-red-400"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </ContextMenuItem>
          </>
        )}
      </>
    )

    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            ref={ref}
            draggable={draggable}
            onDragStart={handleDragStart}
            onDragEnd={onDragEnd}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            className={cn(
              'group relative cursor-pointer',
              isDragging && 'opacity-50',
              className
            )}
          >
            <FluentCard
              hover
              className={cn(
                '!p-4 h-full',
                isSelected && 'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-neutral-900'
              )}
            >
              {/* Drag Handle */}
              {draggable && (
                <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">
                  <GripVertical className="h-4 w-4 text-neutral-400" />
                </div>
              )}

              {/* Header with Type Icon and Actions */}
              <div className="flex items-start justify-between mb-3">
                <div className={cn('p-2.5 rounded-xl bg-gradient-to-br', color)}>
                  <Icon className="h-5 w-5 text-white" />
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      onClick={(e) => e.stopPropagation()}
                      className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <MoreVertical className="h-4 w-4 text-neutral-400" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    {onView && (
                      <DropdownMenuItem onClick={onView}>
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </DropdownMenuItem>
                    )}
                    {onEdit && (
                      <DropdownMenuItem onClick={onEdit}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                    )}
                    {onDuplicate && (
                      <DropdownMenuItem onClick={onDuplicate}>
                        <Copy className="h-4 w-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                    )}
                    {onMove && (
                      <DropdownMenuItem onClick={onMove}>
                        <Move className="h-4 w-4 mr-2" />
                        Move
                      </DropdownMenuItem>
                    )}
                    {onDelete && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={onDelete}
                          className="text-red-600 dark:text-red-400"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Preview Image (for images/slides) */}
              {previewImage && (
                <div className="mb-3 -mx-1 aspect-video rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                  <img
                    src={previewImage}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              )}

              {/* Name */}
              <h3 className="font-medium text-neutral-900 dark:text-white truncate mb-1">
                {item.name}
              </h3>

              {/* Type Badge */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400">
                  {typeLabel}
                </span>
              </div>

              {/* Preview Text (for text-based items) */}
              {previewText && !previewImage && (
                <p className="text-xs text-neutral-500 dark:text-neutral-400 line-clamp-2 mb-3">
                  {previewText}
                </p>
              )}

              {/* Footer: Updated date and tags */}
              <div className="mt-auto pt-2 border-t border-neutral-100 dark:border-neutral-800">
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                  Updated {new Date(item.updatedAt).toLocaleDateString()}
                </p>

                {/* Tags */}
                {item.tags && item.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {item.tags.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400"
                      >
                        {tag}
                      </span>
                    ))}
                    {item.tags.length > 2 && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400">
                        +{item.tags.length - 2}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </FluentCard>
          </div>
        </ContextMenuTrigger>

        <ContextMenuContent className="w-48">{menuContent}</ContextMenuContent>
      </ContextMenu>
    )
  }
)

ContentItemCard.displayName = 'ContentItemCard'

export default ContentItemCard
